import React from 'react';
import { motion } from 'framer-motion';
import { 
  ShieldCheckIcon, 
  GlobeAltIcon, 
  UserGroupIcon,
  TrophyIcon,
  BuildingLibraryIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  LightBulbIcon,
  HeartIcon,
  StarIcon,
  ArrowTrendingUpIcon,
  BanknotesIcon,
  ScaleIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

// Import images
import teamImage from '../assets/diverse-group-business-professionals-posing-together-monitoring-room_482257-94131.jpg_kxystx.jpg';
import analysisImage from '../assets/diverse-teammates-examining-statistics-infographics-computer_482257-90942.jpg_z2rlyq.jpg';
import officeImage from '../assets/teammates-working-late-office_23-2148991374.jpg_vty8zw.jpg';
import businessImage from '../assets/business-person-futuristic-business-environment.jpg';
import groupProfessionalsImage from '../assets/business-team-singapore.jpg';
import analystTeamImage from '../assets/analyst-team-utilize-bi-fintech-analyze-financial-data-ornamented-meeting_31965-377645.jpg_hqqshc.jpg';
import investmentConceptImage from '../assets/invest-investment-financial-income-profit-costs-concept_53876-128112.jpg_ilkdzr.jpg';

const About = () => {
  const stats = [
    { label: 'Years of Excellence', value: '15+', icon: TrophyIcon },
    { label: 'Global Offices', value: '25', icon: BuildingLibraryIcon },
    { label: 'Professional Staff', value: '2,500+', icon: UserGroupIcon },
    { label: 'Assets Under Management', value: '$500B+', icon: BanknotesIcon },
    { label: 'Daily Trading Volume', value: '$12.5B+', icon: ChartBarIcon },
    { label: 'Client Satisfaction', value: '98.7%', icon: StarIcon }
  ];

  const values = [
    {
      icon: ShieldCheckIcon,
      title: 'Integrity First',
      description: 'We operate with unwavering ethical standards, ensuring transparency and trust in every interaction with our clients.',
      gradient: 'from-blue-500 to-blue-600'
    },
    {
      icon: LightBulbIcon,
      title: 'Innovation Drive',
      description: 'Continuously pushing the boundaries of financial technology to deliver cutting-edge trading solutions.',
      gradient: 'from-yellow-500 to-yellow-600'
    },
    {
      icon: UserGroupIcon,
      title: 'Client Excellence',
      description: 'Our clients success is our success. We provide personalized service and support to help achieve financial goals.',
      gradient: 'from-green-500 to-green-600'
    },
    {
      icon: GlobeAltIcon,
      title: 'Global Reach',
      description: 'Connecting traders worldwide with comprehensive market access and localized support in every region.',
      gradient: 'from-purple-500 to-purple-600'
    },
    {
      icon: ScaleIcon,
      title: 'Regulatory Excellence',
      description: 'Maintaining the highest regulatory standards and compliance across all jurisdictions we operate in.',
      gradient: 'from-red-500 to-red-600'
    },
    {
      icon: HeartIcon,
      title: 'Social Responsibility',
      description: 'Committed to sustainable business practices and giving back to the communities we serve.',
      gradient: 'from-pink-500 to-pink-600'
    }
  ];

  const leadership = [
    {
      name: 'Marcus Johnson',
      role: 'Chief Executive Officer',
      bio: 'Former Goldman Sachs Managing Director with 20+ years in institutional trading and risk management.',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&h=300&fit=crop&crop=face',
      experience: '20+ Years',
      specialization: 'Strategic Leadership'
    },
    {
      name: 'Sarah Chen',
      role: 'Chief Technology Officer',
      bio: 'Ex-Google engineer who led the development of high-frequency trading systems at top-tier investment banks.',
      image: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=300&h=300&fit=crop&crop=face',
      experience: '15+ Years',
      specialization: 'Financial Technology'
    },
    {
      name: 'David Rodriguez',
      role: 'Chief Risk Officer',
      bio: 'Previously Head of Risk at JPMorgan Chase, specializing in market risk and regulatory compliance.',
      image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=300&h=300&fit=crop&crop=face',
      experience: '18+ Years',
      specialization: 'Risk Management'
    },
    {
      name: 'Emily Thompson',
      role: 'Chief Financial Officer',
      bio: 'Former Morgan Stanley Vice President with expertise in corporate finance and international markets.',
      image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=300&h=300&fit=crop&crop=face',
      experience: '16+ Years',
      specialization: 'Corporate Finance'
    }
  ];

  const timeline = [
    {
      year: '2009',
      title: 'Foundation',
              description: 'IMEXS BROKERAGE founded with a vision to democratize professional trading tools.',
      icon: BuildingLibraryIcon
    },
    {
      year: '2012',
      title: 'Global Expansion',
      description: 'Launched international operations across Europe and Asia Pacific regions.',
      icon: GlobeAltIcon
    },
    {
      year: '2015',
      title: 'Technology Revolution',
      description: 'Introduced AI-powered trading algorithms and machine learning analytics.',
      icon: LightBulbIcon
    },
    {
      year: '2018',
      title: 'Crypto Integration',
      description: 'First traditional broker to offer comprehensive cryptocurrency trading solutions.',
      icon: CurrencyDollarIcon
    },
    {
      year: '2021',
      title: 'Mobile Innovation',
      description: 'Launched award-winning mobile trading platform with advanced features.',
      icon: UserGroupIcon
    },
    {
      year: '2024',
      title: 'Market Leadership',
      description: 'Recognized as the top trading platform by multiple industry organizations.',
      icon: TrophyIcon
    }
  ];

  const achievements = [
    { title: 'Best Trading Platform 2024', organization: 'FinTech Excellence Awards', year: '2024' },
    { title: 'Most Innovative Brokerage', organization: 'Global Finance Awards', year: '2023' },
    { title: 'Top Security Implementation', organization: 'CyberSecurity Institute', year: '2023' },
    { title: 'Excellence in Customer Service', organization: 'Industry Choice Awards', year: '2022' },
    { title: 'Technology Innovation Leader', organization: 'TradeTech Summit', year: '2022' },
    { title: 'Best Mobile Trading App', organization: 'Mobile Finance Awards', year: '2021' }
  ];

  return (
    <div className="min-h-screen bg-white w-full overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative py-20 sm:py-32 bg-gradient-to-br from-primary-900 via-primary-800 to-primary-700 overflow-hidden mt-20">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-black/40"></div>
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-30"
            style={{ backgroundImage: `url(${businessImage})` }}
          ></div>
        </div>
        
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 w-full">
          <div className="text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-6 sm:mb-8 leading-tight">
                Pioneering the Future of
                <span className="block text-transparent bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text">
                  Financial Trading
                </span>
              </h1>
              <p className="text-base sm:text-lg lg:text-xl text-gray-200 max-w-4xl mx-auto leading-relaxed px-4">
                For over 15 years, IMEXS BROKERAGE has been at the forefront of financial innovation, 
                providing institutional-grade trading solutions to professionals worldwide.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gray-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center group"
              >
                <div className="flex justify-center mb-4">
                  <div className="p-4 rounded-xl bg-primary-100 group-hover:bg-primary-200 transition-colors duration-300">
                    <stat.icon className="h-8 w-8 text-primary-600" />
                  </div>
                </div>
                <div className="text-4xl font-bold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors duration-300">
                  {stat.value}
                </div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-8">
                Our Story of Excellence
              </h2>
              <div className="space-y-6 text-base sm:text-lg text-gray-600 leading-relaxed">
                <p>
                  Founded in 2009 by a team of Wall Street veterans and technology innovators, 
                  IMEXS BROKERAGE emerged from a simple yet powerful vision: to democratize 
                  access to professional-grade trading tools and market intelligence.
                </p>
                <p>
                  What started as a boutique firm serving institutional clients has evolved into 
                  a global financial technology leader, serving over 250,000 active traders across 
                  190 countries. Our commitment to innovation, security, and client success has 
                  earned us recognition as the industry's most trusted trading platform.
                </p>
                <p>
                  Today, we manage over $500 billion in assets and process more than $12.5 billion 
                  in daily trading volume, while maintaining our core values of integrity, 
                  innovation, and client-first service.
                </p>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <img 
                src={teamImage} 
                alt="Our Team" 
                className="rounded-xl shadow-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-primary-600/20 to-transparent rounded-xl"></div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 bg-gray-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-20"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Our Core Values
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The principles that guide every decision we make and every service we deliver.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                className="group"
              >
                <div className="card hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 relative overflow-hidden">
                  <div className={`absolute top-0 left-0 w-full h-1 bg-gradient-to-r ${value.gradient}`}></div>
                  <div className="flex items-center mb-6">
                    <div className={`p-4 rounded-xl bg-gradient-to-r ${value.gradient} shadow-lg`}>
                      <value.icon className="h-8 w-8 text-white" />
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-primary-600 transition-colors">
                    {value.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed text-lg">
                    {value.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership Team */}
      <section className="py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-20"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              World-Class Leadership
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our executive team brings decades of experience from top-tier financial institutions 
              and technology companies.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
            {leadership.map((leader, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="group"
              >
                <div className="card text-center hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                  <div className="relative mb-6">
                    <img 
                      src={leader.image} 
                      alt={leader.name}
                      className="w-32 h-32 rounded-full mx-auto object-cover border-4 border-primary-100 group-hover:border-primary-300 transition-colors duration-300"
                    />
                    <div className="absolute inset-0 rounded-full bg-primary-600/0 group-hover:bg-primary-600/10 transition-all duration-300"></div>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{leader.name}</h3>
                  <div className="text-primary-600 font-semibold mb-4">{leader.role}</div>
                  <p className="text-gray-600 mb-6 leading-relaxed">{leader.bio}</p>
                  <div className="flex justify-between text-sm">
                    <div className="text-center">
                      <div className="font-bold text-primary-600">{leader.experience}</div>
                      <div className="text-gray-500">Experience</div>
                    </div>
                    <div className="text-center">
                      <div className="font-bold text-primary-600">{leader.specialization}</div>
                      <div className="text-gray-500">Expertise</div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Company Timeline */}
      <section className="py-24 bg-gray-900 text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-20"
          >
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">
              Our Journey of Innovation
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Key milestones that have shaped our evolution into a global financial technology leader.
            </p>
          </motion.div>

          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-primary-600"></div>
            
            <div className="space-y-16">
              {timeline.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.2 }}
                  className={`flex items-center ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}
                >
                  <div className={`w-1/2 ${index % 2 === 0 ? 'pr-12 text-right' : 'pl-12 text-left'}`}>
                    <div className="card-dark border-gray-700 hover:border-primary-500 transition-colors duration-300">
                      <div className="flex items-center mb-4">
                        <div className="p-3 rounded-lg bg-primary-600 mr-4">
                          <item.icon className="h-6 w-6 text-white" />
                        </div>
                        <div className="text-2xl font-bold text-primary-400">{item.year}</div>
                      </div>
                      <h3 className="text-xl font-bold text-white mb-3">{item.title}</h3>
                      <p className="text-gray-300 leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                  
                  {/* Timeline Dot */}
                  <div className="relative z-10">
                    <div className="w-6 h-6 bg-primary-600 rounded-full border-4 border-gray-900"></div>
                  </div>
                  
                  <div className="w-1/2"></div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Awards & Recognition */}
      <section className="py-24 bg-gradient-to-br from-primary-50 to-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-20"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Industry Recognition
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our commitment to excellence has been recognized by leading industry organizations worldwide.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {achievements.map((achievement, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group"
              >
                <div className="card hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 text-center">
                  <TrophyIcon className="h-16 w-16 text-yellow-500 mx-auto mb-6 group-hover:scale-110 transition-transform duration-300" />
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{achievement.title}</h3>
                  <p className="text-primary-600 font-semibold mb-2">{achievement.organization}</p>
                  <p className="text-gray-500">{achievement.year}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Global Presence */}
      <section className="py-24 bg-gray-900 text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-4xl lg:text-5xl font-bold mb-8">
                Global Presence, Local Expertise
              </h2>
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                With offices across North America, Europe, Asia, and Australia, we provide 
                24/7 support and localized services to our global client base.
              </p>
              
              <div className="grid grid-cols-2 gap-8">
                <div className="text-center">
                  <div className="text-4xl font-bold text-primary-400 mb-2">25</div>
                  <div className="text-gray-300">Global Offices</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-primary-400 mb-2">190+</div>
                  <div className="text-gray-300">Countries Served</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-primary-400 mb-2">50+</div>
                  <div className="text-gray-300">Languages Supported</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-primary-400 mb-2">24/7</div>
                  <div className="text-gray-300">Global Support</div>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <img 
                src={analysisImage} 
                alt="Global Operations" 
                className="rounded-xl shadow-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-primary-600/20 to-transparent rounded-xl"></div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-primary-600 to-primary-700">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-8">
              Join the IMEXS BROKERAGE Family
            </h2>
            <p className="text-xl text-gray-200 mb-12 max-w-3xl mx-auto leading-relaxed">
              Experience the difference of working with a brokerage that puts your success first. 
              Discover why over 250,000 traders trust us with their financial future.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <button className="bg-white text-primary-600 hover:bg-gray-100 px-10 py-4 rounded-lg font-bold text-lg transition-colors duration-300">
                Start Trading Today
              </button>
              <button className="border-2 border-white text-white hover:bg-white hover:text-primary-600 px-10 py-4 rounded-lg font-bold text-lg transition-all duration-300">
                Contact Our Team
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About; 